import React, { useEffect, useState } from "react";
import { GetMonazamLedgerTransactions } from "../../../../../Actions/LedgerActions";
import { formatAmount, SuccessToast, WarningToast } from "../../../../../Util";
import { Spinner } from "react-bootstrap";
import { FaArrowRight } from "react-icons/fa";
import { ClaimUnclaimedRequest, GetRegisteredHGOs } from "../../../../../Actions/RequestActions";
import EditLedgerModal from "./EditLedgerModal";
import { isEditableMonazamLedgerTransaction } from "./ledgerEditEligibility";

// Helper function to check if bankCredit is valid (including 0, "0", "0.00")
const hasValidBankCredit = (bankCredit) => {
  if (bankCredit === null || bankCredit === undefined || bankCredit === "") return false;
  const numValue = parseFloat(bankCredit);
  return !isNaN(numValue) && numValue >= 0;
};

// Helper function to check if transaction is accumulative or reversal accumulative
const isAccumulativeTransaction = (transaction) => {
  return transaction.accumulativeRequestId || 
         transaction.categoryType?.includes("Transfer") ||
         transaction.type?.includes("Transfer");
};

// Helper function to check if transaction is debit
const isDebitTransaction = (transaction) => {
  return transaction.debit !== null && transaction.debit !== undefined;
};

const getRowHighlightStyle = (transactionId, highlightedTransactionId) => ({
  backgroundColor:
    highlightedTransactionId && transactionId === highlightedTransactionId
      ? "#ffecb3"
      : undefined,
});

const EditLedgerButton = ({ transaction, isMonazam, inCombinedTable, onEdit }) => {
  if (!isMonazam || !isEditableMonazamLedgerTransaction(transaction, { inCombinedTable })) {
    return null;
  }

  return (
    <button
      type="button"
      className="btn btn-sm btn-compact"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(transaction);
      }}
    >
      Edit
    </button>
  );
};

const ClaimModal = ({ isOpen, onClose, request, onClaim }) => {
  const [selectedHgoId, setSelectedHgoId] = useState("");
  const [hgos, setHGOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      GetRegisteredHGOs(
        setHGOs,
        setLoading,
        setError
      );
    }
  }, [isOpen]);

  const handleClaim = () => {
    if (!selectedHgoId) {
      WarningToast("Please select an HGO company");
      return;
    }
    onClaim(selectedHgoId);
    setSelectedHgoId("");
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" onClick={onClose} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Select HGO</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">HGO Company *</label>
              <select
                className="form-select"
                value={selectedHgoId}
                onChange={(e) => setSelectedHgoId(e.target.value)}
                disabled={loading}
              >
                <option value="" disabled hidden>
                  {loading ? "Loading HGOs..." : "Select"}
                </option>
                {hgos.map((hgo) => (
                  <option key={hgo.id} value={hgo.id}>
                    {hgo.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-success" onClick={handleClaim}>
              Claim
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnclaimedTransactionRow = ({ transaction, index, currency, onClaimClick, isMonazam, onEdit, highlightedTransactionId }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <tr 
      key={transaction.id || `transaction-${index}`}
      style={{ 
        cursor: 'pointer',
        backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
        transition: 'background-color 0.2s ease',
        ...getRowHighlightStyle(transaction.id, highlightedTransactionId),
      }}
      onClick={() => onClaimClick(transaction)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <td className="tabledata-txt py-3">
        {transaction.date?.split("T")[0]}
      </td>
      <td className="tabledata-txt py-3 monazam-narration-col">
        {transaction.narration}
      </td>
      <td className="tabledata-txt py-3 monazam-bank-name-col">
        {transaction.bankName || "-"}
      </td>
      <td className="tabledata-txt py-3 monazam-debit-col">
        {formatAmount(transaction.debit)}
      </td>
      <td className="tabledata-txt py-3 monazam-credit-col">
        {formatAmount(transaction.credit)}
      </td>
      <td className="tabledata-txt py-3 monazam-balance-col">
        {formatAmount(transaction.balance || 0)}
      </td>
      {isMonazam && (
        <td className="tabledata-txt py-3" style={{ width: "70px" }}>
          <EditLedgerButton
            transaction={transaction}
            isMonazam={isMonazam}
            onEdit={onEdit}
          />
        </td>
      )}
      <td className="tabledata-txt py-3" style={{ width: '40px', position: 'relative' }}>
        <FaArrowRight 
          style={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
            cursor: 'pointer',
            fontSize: '18px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClaimClick(transaction);
          }}
        />
      </td>
    </tr>
  );
};

const RenderUser = ({ user, currency, bankAccountId, isMonazam = true, onUnclaimedClick, onEdit, highlightedTransactionId }) => {
  const [open, setOpen] = useState(false);
  const isUnclaimedUser = user.name === "Unclaimed";

  const calculateRunningBalance = (transactionsList, currency, isHGO = false) => {
    // For sub-ledgers, process transactions to add bank charges rows after main transactions
    const processedTransactions = [];
    
     transactionsList.forEach((transaction) => {
       // First add the main transaction
       processedTransactions.push(transaction);
       // Then add bank charges row if applicable (after the main transaction)
       // Only add bank charges row if NOT a debit transaction
       if (!isDebitTransaction(transaction) && isAccumulativeTransaction(transaction) && hasValidBankCredit(transaction.bankCredit)) {
         processedTransactions.push({
           ...transaction,
           isBankCharge: true
         });
       }
     });
    
    return processedTransactions;
  };

  const sortedTransactions = [...(user.transactions || [])].sort((a, b) => {
    return new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date);
  });
  const transactionsWithBalance = calculateRunningBalance(sortedTransactions, currency, !isMonazam);
  return (
    <div className="dropdown-section mt-4">
      <div className="flex justify-content-between items-center">
        <div>
          <span className="fw-bold small-text">
            {user.name}
          </span>
        </div>
        <button
          className="bg-white text-black textSizeIcon"
          onClick={() => {
            setOpen(!open);
          }}
        >
          {open ? "-" : "+"}
        </button>
      </div>

      {open && (
        <table className="table mt-2 table-borderless monazam-account-table">
          {isMonazam && (
            <thead>
              <tr>
                <th className="tableheader-txt">Date</th>
                <th className="tableheader-txt monazam-narration-col">Narration</th>
                <th className="tableheader-txt monazam-bank-name-col">Bank Name</th>
                <th className="tableheader-txt monazam-debit-col">Debit</th>
                <th className="tableheader-txt monazam-credit-col">Credit</th>
                <th className="tableheader-txt monazam-balance-col">Balance</th>
                <th className="tableheader-txt" style={{ width: "70px" }}>Edit</th>
                {isUnclaimedUser && <th className="tableheader-txt" style={{ width: "40px" }}></th>}
              </tr>
            </thead>
          )}
          <tbody>
            {transactionsWithBalance.map((transaction, index) => {
              if (transaction.isBankCharge) {
                return (
                  <tr key={`bankcharge-${transaction.id || index}`}>
                    <td className="tabledata-txt py-3">
                      {transaction.date?.split("T")[0]}
                    </td>
                    <td className="tabledata-txt py-3">Bank Charges</td>
                    <td className="tabledata-txt py-3"></td>
                    <td className="tabledata-txt py-3"></td>
                    <td className="tabledata-txt py-3">
                      {formatAmount(transaction.bankCredit) || "0"}
                    </td>
                    <td className="tabledata-txt py-3">
                      {formatAmount(transaction.balance || 0)}
                    </td>
                    {isMonazam && <td className="tabledata-txt py-3"></td>}
                    {isUnclaimedUser && <td className="tabledata-txt py-3"></td>}
                  </tr>
                );
              }
              // If this is an unclaimed user's transaction, show it with hover arrow
              if (isUnclaimedUser && onUnclaimedClick) {
                return (
                  <UnclaimedTransactionRow
                    key={transaction.id || `transaction-${index}`}
                    transaction={transaction}
                    index={index}
                    currency={currency}
                    onClaimClick={onUnclaimedClick}
                    isMonazam={isMonazam}
                    onEdit={onEdit}
                    highlightedTransactionId={highlightedTransactionId}
                  />
                );
              }
              
               // For main transaction row in sub-ledger: balance = transaction.balance + bankCredit (if applicable and NOT debit)
               const subLedgerBalance = parseFloat(transaction.balance || 0);
               const subLedgerBankCredit = parseFloat(transaction.bankCredit || 0);
               const isDebit = isDebitTransaction(transaction);
               const mainSubLedgerBalance = !isDebit && isAccumulativeTransaction(transaction) && hasValidBankCredit(transaction.bankCredit)
                 ? subLedgerBalance + subLedgerBankCredit
                 : subLedgerBalance;
              
              return (
                <tr
                  key={transaction.id || `transaction-${index}`}
                  style={getRowHighlightStyle(transaction.id, highlightedTransactionId)}
                >
                  <td className="tabledata-txt py-3">
                    {transaction.date?.split("T")[0]}
                  </td>
                  <td className="tabledata-txt py-3 monazam-narration-col">
                    {transaction.narration}
                  </td>
                  <td className="tabledata-txt py-3 monazam-bank-name-col">
                    {transaction.bankName || "-"}
                  </td>
                  <td className="tabledata-txt py-3 monazam-debit-col">
                    {formatAmount(transaction.debit)}
                  </td>
                  <td className="tabledata-txt py-3 monazam-credit-col">
                    {formatAmount(transaction.credit)}
                  </td>
                  <td className="tabledata-txt py-3 monazam-balance-col">
                    {formatAmount(mainSubLedgerBalance)}
                  </td>
                  {isMonazam && (
                    <td className="tabledata-txt py-3">
                      <EditLedgerButton
                        transaction={transaction}
                        isMonazam={isMonazam}
                        onEdit={onEdit}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Table = ({ setAmount, currency, bankAccountId, setBankAccounts, isMonazam = true }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [selectedUnclaimedRequest, setSelectedUnclaimedRequest] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditTransaction, setSelectedEditTransaction] = useState(null);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState(null);

  const processTransactionsForCombinedTable = (transactionsList) => {
    // Process transactions to add isBankCharge flag for combined table
    const processedTransactions = [];
    
    transactionsList.forEach((transaction) => {
      // First add the main transaction row
      processedTransactions.push(transaction);
      // Then add bank charges row if applicable (after the main transaction)
      // Only add bank charges row if NOT a debit transaction
      if (!isDebitTransaction(transaction) && isAccumulativeTransaction(transaction) && hasValidBankCredit(transaction.bankCredit)) {
        processedTransactions.push({
          ...transaction,
          isBankCharge: true
        });
      }
    });
    
    return processedTransactions;
  };

  const fetchTransactions = () => {
    // For Monazam users, require bankAccountId for PKR
    // For HGO users, allow API call without bankAccountId initially to get bank accounts
    if (isMonazam && currency === "PKR" && (!bankAccountId || bankAccountId === "")) {
      return;
    }
    setLoading(true);
    GetMonazamLedgerTransactions(
      (data) => {
        const sortedTransactions = [...data].sort((a, b) => {
          return new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date);
        });
        const processedTransactions = processTransactionsForCombinedTable(sortedTransactions);
        setTransactions(processedTransactions);
      },
      setUsers,
      setAmount,
      currency,
      bankAccountId || undefined,
      setLoading,
      setError,
      setBankAccounts,
      isMonazam
    );
  };

  useEffect(() => {
    fetchTransactions();
  }, [currency, bankAccountId]);

  const handleUnclaimedClick = (transaction) => {
    // Convert transaction to unclaimed request format for the modal
    const unclaimedRequest = {
      id: transaction.unclaimedRequestId || transaction.id,
      date: transaction.date,
      time: transaction.time,
      narration: transaction.narration,
      currency: transaction.currency,
      amount: transaction.credit || transaction.debit,
      bankName: transaction.bankName,
      iban: transaction.iban,
      transactionId: transaction.transactionId
    };
    setSelectedUnclaimedRequest(unclaimedRequest);
    setClaimModalOpen(true);
  };

  const handleClaim = (hgoId) => {
    if (!selectedUnclaimedRequest) return;
    
    setClaiming(true);
    ClaimUnclaimedRequest(
      selectedUnclaimedRequest.id,
      hgoId,
      (errorMessage) => {
        setClaiming(false);
        if (errorMessage) {
          WarningToast(errorMessage);
        }
      },
      () => {
        setClaiming(false);
        setClaimModalOpen(false);
        setSelectedUnclaimedRequest(null);
        fetchTransactions();
      }
    );
  };

  const handleEditClick = (transaction) => {
    setSelectedEditTransaction(transaction);
    setEditModalOpen(true);
  };

  const handleEditSuccess = (transactionId) => {
    setHighlightedTransactionId(transactionId);
    SuccessToast("Ledger transaction updated successfully");
    fetchTransactions();
  };

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center loading-container">
        <Spinner animation="border" className="spinner-green" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  else if (!!error) return <p>{error}</p>;
  else
    return (
      <div>
        <ClaimModal
          isOpen={claimModalOpen}
          onClose={() => {
            setClaimModalOpen(false);
            setSelectedUnclaimedRequest(null);
          }}
          request={selectedUnclaimedRequest}
          onClaim={handleClaim}
        />
        <EditLedgerModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedEditTransaction(null);
          }}
          transaction={selectedEditTransaction}
          currency={currency}
          onSuccess={handleEditSuccess}
        />
        <div className="table-responsive">
          <table className="table table-sm table-borderless monazam-account-table">
            <thead>
              <tr>
                <th className="tableheader-txt">Date</th>
                <th className="tableheader-txt monazam-narration-col">Narration</th>
                <th className="tableheader-txt monazam-bank-name-col">Bank Name</th>
                <th className="tableheader-txt monazam-debit-col">Debit</th>
                <th className="tableheader-txt monazam-credit-col">Credit</th>
                <th className="tableheader-txt monazam-balance-col">Balance</th>
                {isMonazam && <th className="tableheader-txt" style={{ width: "70px" }}>Edit</th>}
                {isMonazam && <th className="tableheader-txt" style={{ width: '40px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {/* Combined Table: Show all transactions for both Monazam and HGO portals */}
              {transactions?.map((transaction, index) => {
                  if (transaction.isBankCharge) {
                    return (
                      <tr key={`bankcharge-${transaction.id || index}`}>
                        <td className="tabledata-txt py-3">
                          {transaction.date?.split("T")[0]}
                        </td>
                        <td className="tabledata-txt py-3 monazam-narration-col">Bank Charges</td>
                        <td className="tabledata-txt py-3 monazam-bank-name-col"></td>
                        <td className="tabledata-txt py-3 monazam-debit-col"></td>
                        <td className="tabledata-txt py-3 monazam-credit-col">
                          {formatAmount(transaction.bankCredit) || "0"}
                        </td>
                        <td className="tabledata-txt py-3 monazam-balance-col">
                          {formatAmount(currency === "PKR" ? transaction.pkrMonazamLedgerAmount : transaction.usdMonazamLedgerAmount)}
                        </td>
                        {isMonazam && <td className="tabledata-txt py-3"></td>}
                        {isMonazam && <td className="tabledata-txt py-3"></td>}
                      </tr>
                    );
                  }
                  // Check if this transaction is from an unclaimed request
                  // We'll need to check if it's in the "Unclaimed" user's transactions
                  // For now, we'll add the arrow column but only show it for unclaimed transactions
                   // For main transaction row: balance = ledgerAmount + bankCredit (if applicable and NOT debit)
                   const ledgerAmount = currency === "PKR" 
                     ? parseFloat(transaction.pkrMonazamLedgerAmount || 0)
                     : parseFloat(transaction.usdMonazamLedgerAmount || 0);
                   const bankCredit = parseFloat(transaction.bankCredit || 0);
                   const isDebit = isDebitTransaction(transaction);
                   const mainTransactionBalance = !isDebit && isAccumulativeTransaction(transaction) && hasValidBankCredit(transaction.bankCredit)
                     ? ledgerAmount + bankCredit
                     : ledgerAmount;
                  
                  return (
                    <tr
                      key={transaction.id || `transaction-${index}`}
                      style={getRowHighlightStyle(transaction.id, highlightedTransactionId)}
                    >
                      <td className="tabledata-txt py-3">
                        {transaction.date?.split("T")[0]}
                      </td>
                      <td className="tabledata-txt py-3 monazam-narration-col">
                        {transaction.narration}
                      </td>
                      <td className="tabledata-txt py-3 monazam-bank-name-col">
                        {transaction.bankName || "-"}
                      </td>
                      <td className="tabledata-txt py-3 monazam-debit-col">
                        {formatAmount(transaction.debit)}
                      </td>
                      <td className="tabledata-txt py-3 monazam-credit-col">
                        {formatAmount(transaction.credit)}
                      </td>
                      <td className="tabledata-txt py-3 monazam-balance-col">
                        {formatAmount(mainTransactionBalance)}
                      </td>
                      {isMonazam && (
                        <td className="tabledata-txt py-3">
                          <EditLedgerButton
                            transaction={transaction}
                            isMonazam={isMonazam}
                            inCombinedTable
                            onEdit={handleEditClick}
                          />
                        </td>
                      )}
                      {isMonazam && <td className="tabledata-txt py-3" style={{ width: '40px' }}></td>}
                    </tr>
                  );
                })
              }
              {/* <tr>
              <td className="tabledata-txt">02-01-2024</td>
              <td className="tabledata-txt">Transfer</td>
              <td className="tabledata-txt">PKR</td>
              <td className="tabledata-txt">5000</td>
              <td className="tabledata-txt">V002</td>
              <td className="tabledata-txt">Pending</td>
              <td className="tabledata-txt">
                <button className="btn btn-sm me-2 btn-compact">
                  Approve
                </button>
                <button className="btn btn-sm btn-compactreject">
                  Reject
                </button>
              </td>
            </tr> */}
            </tbody>
          </table>
          {/* Sub-ledgers: Show all for Monazam, only own for HGO */}
          {(() => {
            let usersToShow = [];
            
            if (isMonazam) {
              // Monazam Portal: Show all HGO sub-ledgers, with Unclaimed last, and filter empty ones
              // Show Unclaimed sub-ledger only if it has transactions for the selected currency
              usersToShow = users
                .filter(user => {
                  // Filter out empty sub-ledgers (no transactions for the selected currency)
                  return user.transactions && user.transactions.length > 0;
                })
                .sort((a, b) => {
                  // Sort so "Unclaimed" appears last
                  if (a.name === "Unclaimed") return 1;
                  if (b.name === "Unclaimed") return -1;
                  return 0;
                });
            } else {
              // HGO Portal: Show only the logged-in HGO's sub-ledger (if it has transactions)
              if (users.length > 0 && users[0]?.transactions && users[0].transactions.length > 0) {
                usersToShow = [users[0]];
              }
            }
            
            return usersToShow.map((tuser, index) => {
              return (
                <React.Fragment key={tuser.id || `user-${index}`}>
                  <RenderUser 
                    user={tuser} 
                    currency={currency} 
                    bankAccountId={bankAccountId} 
                    isMonazam={isMonazam}
                    onUnclaimedClick={tuser.name === "Unclaimed" ? handleUnclaimedClick : null}
                    onEdit={handleEditClick}
                    highlightedTransactionId={highlightedTransactionId}
                  />
                  <hr />
                </React.Fragment>
              );
            });
          })()}
        </div>
      </div>
    );
};

export default Table;
